import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LegalPageLayout from '@/components/LegalPageLayout';
import { useAcceptanceLog } from '@/hooks/useAcceptanceLog';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { logAcceptance } = useAcceptanceLog();

  const handleAccept = () => {
    logAcceptance('privacyPolicy', 'a Política de Privacidade');
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>Política de Privacidade | Aceena</title>
        <meta name="description" content="Política de Privacidade da Aceena. Saiba como coletamos, usamos e protegemos seus dados pessoais." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <LegalPageLayout
        title="Política de Privacidade"
        lastUpdated="23 de junho de 2026"
        onAccept={handleAccept}
      >
        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introdução</h2>
          <p className="text-foreground/80 leading-relaxed">
            A <strong>Aceena B2W Ltda.</strong>, inscrita no CNPJ sob o nº <strong>42.580.265/0001-56</strong> ("Aceena", "nós", "nosso"), atua como consultoria e intermediação em locação e assinatura de veículos e respeita a privacidade dos titulares de dados pessoais ("você", "seu", "titular"). Esta Política de Privacidade explica como coletamos, usamos, compartilhamos, armazenamos e protegemos suas informações, em conformidade com a Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais – LGPD).
          </p>
          <p className="text-foreground/80 leading-relaxed mt-4">
            Para os fins desta Política, a Aceena atua como <strong>controladora</strong> dos dados pessoais tratados no contexto de seus serviços.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">2. Quais informações coletamos</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Podemos coletar os seguintes tipos de informação:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li><strong>Dados de identificação e contato:</strong> nome, e-mail, número de telefone (incluindo o número de WhatsApp), CPF, cidade e endereço.</li>
            <li><strong>Dados da negociação:</strong> veículo de interesse, preferências (modelo, categoria, quilometragem, orçamento), CEP de atendimento, informações fornecidas durante a qualificação e o histórico de propostas.</li>
            <li><strong>Conteúdo de conversas:</strong> mensagens trocadas com a Aceena pelos canais de atendimento, incluindo o WhatsApp.</li>
            <li><strong>Dados de análise de crédito:</strong> informações necessárias à avaliação de elegibilidade junto às locadoras parceiras, quando aplicável.</li>
            <li><strong>Dados de uso e do dispositivo:</strong> páginas visitadas, interações com o site, tipo de dispositivo, sistema operacional, identificadores e dados de rede.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">3. Como coletamos suas informações</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Coletamos dados das seguintes formas:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Diretamente de você, quando preenche formulários no site, solicita contato ou interage conosco.</li>
            <li><strong>Por meio do WhatsApp</strong>, quando você inicia ou mantém uma conversa com a Aceena. Esse canal opera sobre a infraestrutura da Meta Platforms (WhatsApp Business Platform), sujeita também às políticas de privacidade da Meta.</li>
            <li>Por meio de ferramentas de gestão de relacionamento e atendimento que utilizamos para registrar e dar sequência ao seu atendimento.</li>
            <li>A partir de bases de dados de relacionamento anteriores da própria Aceena (ver seção 9).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">4. Finalidades e bases legais do tratamento</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Tratamos seus dados pessoais para as seguintes finalidades, com as respectivas bases legais previstas na LGPD:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li><strong>Prestar o serviço de consultoria e intermediação</strong> (apresentar propostas, qualificar a demanda, intermediar junto às locadoras): execução de contrato ou de procedimentos preliminares a contrato, a seu pedido (art. 7º, V).</li>
            <li><strong>Atendê-lo pelos canais de comunicação, incluindo o WhatsApp:</strong> execução de contrato e legítimo interesse em prestar atendimento eficiente (art. 7º, V e IX).</li>
            <li><strong>Realizar comunicações de marketing, ofertas e promoções</strong> da Aceena e de parceiros: consentimento do titular (art. 7º, I), que pode ser revogado a qualquer momento.</li>
            <li><strong>Cumprir obrigações legais e regulatórias:</strong> cumprimento de obrigação legal ou regulatória (art. 7º, II).</li>
            <li><strong>Garantir a segurança, prevenir fraudes e exercer direitos</strong> em processos: legítimo interesse e exercício regular de direitos (art. 7º, IX e VI).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">5. Uso de inteligência artificial e decisões automatizadas</h2>
          <p className="text-foreground/80 leading-relaxed">
            Parte do nosso atendimento é conduzida por um <strong>assistente virtual de inteligência artificial chamado "Anee"</strong>. A Anee pode receber, interpretar e responder mensagens, bem como organizar e classificar automaticamente o seu interesse para direcionar o atendimento (qualificação inicial).
          </p>
          <p className="text-foreground/80 leading-relaxed mt-4">
            Esse tratamento automatizado tem por objetivo agilizar o atendimento e direcioná-lo à equipe humana adequada. <strong>Nenhuma decisão que afete seus interesses de forma definitiva é tomada exclusivamente de forma automatizada sem possibilidade de revisão humana.</strong> Nos termos do art. 20 da LGPD, você pode solicitar a revisão, por uma pessoa natural, de decisões tomadas com base em tratamento automatizado, bem como informações sobre os critérios utilizados, observados os segredos comercial e industrial.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">6. Com quem compartilhamos suas informações</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Podemos compartilhar seus dados, na medida necessária às finalidades acima, com:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li><strong>Locadoras e parceiros de negócio</strong>, para viabilizar propostas e a contratação do serviço, incluindo Localiza Meoo, Arval, LM, Movida, Original e outras locadoras parceiras.</li>
            <li>
              <strong>Operadores e prestadores de serviço de tecnologia</strong>, que tratam dados em nosso nome e seguindo nossas instruções, incluindo:
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><strong>Meta Platforms / WhatsApp</strong> – canal de mensagens;</li>
                <li><strong>Supabase</strong> – infraestrutura de banco de dados e backend;</li>
                <li><strong>OpenAI</strong> – processamento de linguagem do assistente virtual;</li>
                <li><strong>ElevenLabs</strong> – geração de áudio/voz, quando aplicável;</li>
                <li><strong>RD Station</strong> – gestão de relacionamento e atendimento.</li>
              </ul>
            </li>
            <li><strong>Autoridades públicas e judiciais</strong>, quando exigido por lei, ordem judicial ou para o exercício regular de direitos.</li>
          </ul>
          <p className="text-foreground/80 leading-relaxed mt-4">
            Exigimos que nossos operadores adotem medidas de segurança e tratem os dados apenas para as finalidades por nós determinadas.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">7. Transferência internacional de dados</h2>
          <p className="text-foreground/80 leading-relaxed">
            Alguns dos operadores e tecnologias que utilizamos (como Meta, Supabase, OpenAI e ElevenLabs) podem <strong>armazenar ou processar dados em servidores localizados fora do Brasil</strong>. Nesses casos, a transferência internacional é realizada em conformidade com o art. 33 da LGPD, mediante a adoção de garantias adequadas de proteção e/ou com base nas hipóteses legais aplicáveis, sempre buscando assegurar nível de proteção compatível com a legislação brasileira.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">8. Dados originados de bases de relacionamento anteriores</h2>
          <p className="text-foreground/80 leading-relaxed">
            Parte dos dados que tratamos pode ter sido coletada anteriormente, no contexto de contatos e atendimentos prévios com a Aceena, e migrada para os sistemas atuais (incluindo a plataforma de atendimento da Anee). Esses dados continuam a ser tratados com base nas mesmas finalidades e bases legais descritas nesta Política. Caso você não deseje mais ser contatado, poderá exercer seus direitos a qualquer momento, conforme a seção 10.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">9. Retenção de dados</h2>
          <p className="text-foreground/80 leading-relaxed">
            Mantemos seus dados pessoais pelo tempo necessário ao cumprimento das finalidades descritas nesta Política, ou pelos prazos exigidos por obrigações legais, regulatórias ou para o exercício regular de direitos. Encerradas as finalidades e os prazos legais aplicáveis, os dados são eliminados ou anonimizados.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">10. Seus direitos como titular</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Nos termos do art. 18 da LGPD, você tem o direito de, a qualquer momento e mediante requisição:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>confirmar a existência de tratamento de seus dados;</li>
            <li>acessar seus dados;</li>
            <li>corrigir dados incompletos, inexatos ou desatualizados;</li>
            <li>solicitar a anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a lei;</li>
            <li>solicitar a portabilidade de seus dados;</li>
            <li>solicitar a eliminação dos dados tratados com base no consentimento;</li>
            <li>obter informação sobre as entidades com as quais compartilhamos seus dados;</li>
            <li>revogar o consentimento;</li>
            <li>opor-se a tratamento e solicitar a revisão de decisões automatizadas.</li>
          </ul>
          <p className="text-foreground/80 leading-relaxed mt-4">
            Para exercer seus direitos, entre em contato com nosso Encarregado pelo e-mail indicado na seção 12.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">11. Segurança das informações</h2>
          <p className="text-foreground/80 leading-relaxed">
            Adotamos medidas técnicas e organizacionais para proteger seus dados contra acessos não autorizados, perda, alteração, divulgação ou destruição. Nenhum método de transmissão ou armazenamento é completamente seguro; em caso de incidente de segurança relevante, adotaremos as providências previstas em lei, incluindo a comunicação à Autoridade Nacional de Proteção de Dados (ANPD) e aos titulares, quando aplicável.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">12. Encarregado pelo Tratamento de Dados (DPO)</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">
            Para dúvidas, solicitações ou para o exercício de seus direitos, entre em contato com o nosso Encarregado pela proteção de dados:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>
              <strong>E-mail:</strong>{' '}
              <a href="mailto:contato@aceena.com.br" className="text-aceena-green hover:underline">
                contato@aceena.com.br
              </a>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">13. Cookies e tecnologias semelhantes</h2>
          <p className="text-foreground/80 leading-relaxed">
            Utilizamos cookies e tecnologias semelhantes para melhorar sua experiência, analisar o tráfego do site e personalizar conteúdo. Você pode gerenciar suas preferências por meio das configurações do seu navegador.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">14. Alterações nesta Política</h2>
          <p className="text-foreground/80 leading-relaxed">
            Podemos atualizar esta Política periodicamente. Alterações relevantes serão informadas mediante a publicação da nova versão neste endereço, com atualização da data de "Última atualização".
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">15. Reclamações à ANPD</h2>
          <p className="text-foreground/80 leading-relaxed">
            Sem prejuízo do contato com nosso Encarregado, você pode apresentar reclamações à Autoridade Nacional de Proteção de Dados (ANPD), por meio dos canais oficiais disponíveis em seu site.
          </p>
        </section>

        <hr className="border-border" />

        <p className="italic text-foreground/70 leading-relaxed">
          Ao utilizar nossos serviços ou clicar em "Li e Aceito", você confirma que leu e compreendeu esta Política de Privacidade.
        </p>
      </LegalPageLayout>
    </>
  );
};

export default PrivacyPolicy;
